import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { useGetRequest } from "../../Utils/hooks/QueryHandler";
import { UpgradePlan } from "../../pages/UpgradePlan";
import { AuthorizationCard } from "../../pages/AuthorizationCard";
import { QuotationHeader } from "./QutotationModule/QuotationHeader";
// @ts-ignore
import { QuotationListing } from "./QutotationModule/QuotationListing";

export const Quotations = () => {
  const navigate = useNavigate();

  const [customer, setCustomer] = useLocalStorageState("customer");

  const customer_id = customer?.customer_details?.customer_id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const headers = {
    Authorization: token,
    Authentication: rf,
  };

  const [isMyApproval, setMyApproval] = useState(() => {
    const saved = localStorage.getItem("quotation_isMyApproval");
    return saved === "true";
  });
  const [allQuotation, setQuotation] = useState([]);
  // States for using pagination
  const [limit, setLimit] = useState(25);

  const [allOffset, setAllOffset] = useState(0);
  const [myApprovalOffset, setMyApprovalOffset] = useState(0);

  const [search, setSearch] = useState("");
  // active pagination based on tab
  const activeOffset = isMyApproval ? myApprovalOffset : allOffset;
  const setActiveOffset = isMyApproval ? setMyApprovalOffset : setAllOffset;

  // endpoint and querykey state for fetching data
  const [endpoint, setEndpoint] = useState("");
  const [queryKey, setQueryKey] = useState("");
  // states to store the filter data
  const [filterStates, setFilterState] = useState([]);

  // checking and validating if the last selected filter still exist until then the state initialized would be false and no data or the heading would be renderd
  const [isInitialized, setIsInitialized] = useState(false);

  const [heading, setHeading] = useState("All Quotations");
  // filter state to see the temoporary applied filter data,
  const [tempFilterData, setTempFilterData] = useState(null);

  // filter categories to render within the heading dropdown
  const [filterCategories, setFilterCategories] = useState([]);

  const permission = customer.customer_details?.permission;
  // permissions to handle
  const isQuotation = Boolean(
    permission?.find((c) => c.module_name === "quotation")
  );
  const quotationPermission =
    isQuotation && permission?.filter((c) => c.module_name === "quotation");
  const user_type = Number(customer.customer_details?.user_type);
  const quotationGrant = {
    isView: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "quotation")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "quotation")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "quotation")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "quotation")?.delete)) : true,
    isRevised: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "revised")?.is_action)) : true,
    isImport: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "import")?.is_action)) : true,
    isExport: user_type !== 1 ? Boolean(Number(isQuotation && quotationPermission?.find((c) => c.feature_name === "export")?.is_action)) : true,
  };

  useEffect(() => {
    if (filterCategories?.length > 0 && !isInitialized) {
      const lastHeading = localStorage.getItem("quotation_last_heading");
      const existingFilter = filterCategories.find(
        (filter) => filter.card_name === lastHeading
      );
      setHeading(existingFilter ? lastHeading : "All Quotations");
      setIsInitialized(true);
    }
  }, [filterCategories, isInitialized]);

  const dropdownOptions = [
    { value: "reference_number", label: "Reference Number" },
    { value: "contact_id", label: "Contact" },
    { value: "deal_id", label: "Deal Name" },
    { value: "owner_id", label: "Owner" },
    { value: "quotation_number", label: "Quotation Number" },
    { value: "company_id", label: "Company Name" },
    { value: "quotation_date", label: "Quotation Date", type: "date-range" },
    { value: "expiry_date", label: "Expiry Date", type: "date-range" },
    { value: "created_at", label: "Created at", type: "date-range" },
  ];

  const params = {
    limit,
    offset: activeOffset,
    search,
  };

  useEffect(() => {
    const lowerHeading = heading
      ? heading?.toLowerCase()?.split(" ").join("_")
      : heading;

    if (isMyApproval) {
      setEndpoint(
        `quotation/fetchMyApprovals/${customer_id}/${customer?.customer_details?.id}`
      );
      setQueryKey("getApprovals");
    } else if (lowerHeading === "all_quotations") {
      setEndpoint(
        `quotation/fetchQuotation/${customer_id}/${customer?.customer_details?.id}/${customer?.customer_details?.user_type}`
      );
      setQueryKey("fetchAllQuotations");
    } else {
      const matchedCategory = filterCategories.find(
        (category) => category.card_name === heading
      );
      if (matchedCategory) {
        setEndpoint(
          `quotation/fetchQuotation/${customer_id}/${customer?.customer_details?.id}/${matchedCategory.id}`
        );
        setQueryKey(
          `filterQuery${matchedCategory.card_name
            .toLowerCase()
            ?.split(" ")
            .join("_")}`
        );
      }
    }
  }, [heading, filterCategories, isMyApproval]);

  // Single useGetRequest call
  const {
    data: quotationData,
    refetch: fetchQuotation,
    isLoading
  } = useGetRequest(
    endpoint,
    [queryKey, isMyApproval],
    headers,
    { customer, setCustomer, rf: headers.Authentication, navigate },
    params
  );

  // storing all contacts to a state to use it in the custom dropdown for the dynamic fields
  useEffect(() => {
    if (
      quotationData &&
      quotationData.filter_data &&
      quotationData.filter_data[0] &&
      quotationData.filter_data[0].filter_values
    ) {
      const values = quotationData.filter_data[0].filter_values;
      const parsedState = JSON.parse(values);
      const filteredArray = parsedState?.map((state) => {
        const dropdownOption = dropdownOptions.find(
          (opt) => opt.value === state.label_name
        );

        let mappedOptions = null;
        if (dropdownOption?.option && Array.isArray(dropdownOption.option)) {
          if (dropdownOption.value === "owner_id") {
            mappedOptions = dropdownOption.option?.map((opt) => ({
              value: opt.id,
              label: opt.leadName,
            }));
          } else if (dropdownOption.value === "company_id") {
            mappedOptions = dropdownOption.option?.map((opt) => ({
              value: opt.company_id,
              label: opt.company_name,
            }));
          } else if (dropdownOption.value === "contact_id") {
            mappedOptions = dropdownOption.option?.map((opt) => ({
              value: opt.contactId,
              label: opt.contact_name,
            }));
          } else if (dropdownOption.value === "deal_id") {
            mappedOptions = dropdownOption.option?.map((opt) => ({
              value: opt.id,
              label: opt.deal_name + " " + `(${opt.deal_no})`,
            }));
          }
        }
        return {
          id: state.label_name,
          field: state.label_name,
          label_name: dropdownOption ? dropdownOption.label : state.label_name,
          option: mappedOptions,
          subFilterType: state.label_query,
          value:
            state.label_query === "between"
              ? { start: state.label_value[0], end: state.label_value[1] }
              : state.label_value,
        };
      });
      setFilterState(filteredArray);
    } else {
      setFilterState([]);
    }

    setQuotation(quotationData?.data);
  }, [queryKey, quotationData, isMyApproval]);


  useEffect(() => {
    if (endpoint && queryKey) {
      fetchQuotation();
    }
  }, [isMyApproval, endpoint]);

  useEffect(() => {
    if (tempFilterData !== null) {
      setActiveOffset(0);
    }
  }, [tempFilterData]);

  const columnOptions = [
    // { key: "sn", label: "#",locked: true },
    { key: "quotation_number", label: "Quotation Number", mandatory: true },
    { key: "contact_full_name", label: "Contact" },
    { key: "phone", label: "Contact No" },
    { key: "company_name", label: "Company" },
    { key: "quotation_date", label: "Quotation Date" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "owner_full_name", label: "Lead Owner" },
    { key: "deal_name", label: "Deal" },
    { key: "quotation_status", label: "Quotation Status" },
  ];

  // Initialize columns for the table - ALL columns visible by default
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem("visibleQuotationColumns");
    const allColumnKeys = columnOptions?.map(col => col.label);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing visible columns:", e);
      }
    }
    return allColumnKeys;
  });

  useEffect(() => {
    localStorage.setItem(
      "quotation_isMyApproval",
      isMyApproval ? "true" : "false"
    );
  }, [isMyApproval]);

  // if plan doesn't include quotation then show upgrade plan modal
  if (!isQuotation) {
    return <UpgradePlan moduleName="Quotations" />
  }

  // if view permission isn't given by the admin or the POC then show authorization card.
  if (!quotationGrant?.isView) {
    return <AuthorizationCard />
  }

  return (
    <>
    
      <QuotationHeader
        heading={heading}
        setHeading={setHeading}
        quotationGrant={quotationGrant}
        isMyApproval={isMyApproval}
        setMyApproval={setMyApproval}
        columnOptions={columnOptions}
        setActiveOffset={setActiveOffset}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        columns={columns}
        setColumns={setColumns}
        filterStates={filterStates}
        setFilterState={setFilterState}
        setTempFilterData={setTempFilterData}
        setSearch={setSearch}
      />
      <QuotationListing
        tempFilterData={tempFilterData}
        allQuotation={allQuotation}
        limit={limit}
        setLimit={setLimit}
        columns={columns}
        activeOffset={activeOffset}
        setActiveOffset={setActiveOffset}
        quotationData={quotationData}
        fetchQuotation={fetchQuotation}
        quotationGrant={quotationGrant}
        isLoading={isLoading}
        isMyApproval={isMyApproval}
      />
    </>
  );
};